import React, { useState } from 'react';
import {
  Modal,
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Button
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import ImageAnnotationTool from './ImageAnnotationTool';
import MiddleController from './MiddleController';
import { useDispatch } from 'react-redux';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
// eslint-disable-next-line
import { ADD_CAMERA, SET_CAMERA_IMAGE, CAMERA_ADDED } from 'store/actions';
import { createCamera } from '../../../../../api/FM/videoPageApis';
import defaultImage from '../../../../../assets/images/Camera-013_pathway(entry-samsung)_mobile-zone.jpg';
import axios from 'axios';
// import { json } from 'react-router';

const AddCameraButton = () => {
  const [openAddCamera, setOpenAddCamera] = useState(false);
  const [openAnnotate, setOpenAnnotate] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [cameraAddress, setCameraAddress] = useState('');
  const [framesSkipped, setFramesSkipped] = useState(5);
  const [isActive, setIsActive] = useState(true);
  const [annotationStatus, setAnnotationStatus] = useState(null);
  const [isAnnotateEnabled, setIsAnnotateEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors] = useState({});
  const [cameraImage, setCameraImage] = useState(defaultImage);
  const cameraId = null;
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  const source = (localStorage.getItem('source'));

  const dispatch = useDispatch();

  const handleOpenAddCamera = () => {
    setOpenAddCamera(true);
    // Reset states when modal opens
    setCameraImage(defaultImage);
    setAnnotationStatus(null);
    setIsAnnotateEnabled(false); // Disable annotate by default until API succeeds
  };

  const handleCloseAddCamera = () => {
    setOpenAddCamera(false);
    // Reset form fields
    setDeviceName('');
    setCameraAddress('');
    setFramesSkipped(5);
    setIsActive(true);
    setCameraImage(defaultImage);
    setAnnotationStatus(null);
    setIsAnnotateEnabled(false);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCameraAddressBlur = async () => {
    if (!cameraAddress.trim()) {
      // If empty, show default image but keep annotate disabled
      setCameraImage(defaultImage);
      setIsAnnotateEnabled(false);
      setAnnotationStatus(false);
      setSnackbar({
        open: true,
        message: 'Please enter a valid camera address',
        severity: 'error'
      });
      return;
    }

    const abortController = new AbortController();
    setIsLoading(true);
    setIsAnnotateEnabled(false); // Disable while loading

    try {
      const response = await axios.post(
        'https://db.neophyte.live/createCamera/api/captureFrame',
        { rtsp: cameraAddress, source: source },
        { signal: abortController.signal }
      );

      if (response?.status === 403) {
        setSnackbar({ open: true, message: response.data, severity: 'error' });
        setCameraImage(defaultImage);
        setAnnotationStatus(false);
        setIsAnnotateEnabled(false); // Keep disabled on error
        return;
      }

      const base64Image = response.data.image;
      if (!base64Image) {
        throw new Error('Base64 image data not found in response');
      }

      setCameraImage(base64Image);
      dispatch({ type: 'SET_CAMERA_IMAGE', payload: base64Image });
      setAnnotationStatus(true);
      setSnackbar({ open: true, message: 'Camera connected successfully!', severity: 'success' });
      setIsAnnotateEnabled(true); // Only enable when API succeeds
    } catch (error) {
      console.error('Error fetching camera frame:', error);
      setCameraImage(defaultImage);
      setAnnotationStatus(false);
      setSnackbar({
        open: true,
        message: 'Failed to connect to camera. Please check the address.',
        severity: 'error'
      });
      setIsAnnotateEnabled(false); // Keep disabled on error
    } finally {
      setIsLoading(false);
      abortController.abort();
    }
  };

  const handleCloseAnnotate = () => {
    setOpenAnnotate(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        device_name: deviceName,
        camera_address: cameraAddress,
        process_skip_frame: parseInt(framesSkipped),
        active: isActive
      };
      console.log('Creating camera with data:', data);
      const response = await createCamera(data);
      console.log('Camera created successfully:', response);
      dispatch({ type: CAMERA_ADDED });
      console.log('Dispatched CAMERA_ADDED action');
      handleCloseAddCamera();
      setSnackbar({
        open: true,
        message: 'Camera added successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating camera:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to add camera',
        severity: 'error'
      });
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleOpenAddCamera}
        sx={{
          borderRadius: '8px',
          textTransform: 'none',
          boxShadow: '0 4px 6px rgba(25, 118, 210, 0.1)',
          '&:hover': {
            boxShadow: '0 6px 10px rgba(25, 118, 210, 0.2)'
          }
        }}
      >
        Add Camera
      </Button>

      <Modal open={openAddCamera} onClose={handleCloseAddCamera}>
        <Box
          component="form"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px'
          }}
          onSubmit={handleSubmit}
        >
          <TextField
            label="Device Name"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            fullWidth
            margin="normal"
            error={!!errors.deviceName}
            helperText={errors.deviceName}
            required
          />
          <TextField
            label="Camera Address"
            value={cameraAddress}
            onChange={(e) => setCameraAddress(e.target.value)}
            onBlur={handleCameraAddressBlur}
            fullWidth
            margin="normal"
            helperText="Enter RTSP URL"
            InputProps={{
              endAdornment: (
                <>
                  {isLoading && <CircularProgress size={20} className="text-blue-500" />}
                  {!isLoading && annotationStatus === true && <CheckCircleOutlineIcon className="text-green-500 animate-pulse-once" />}
                  {!isLoading && annotationStatus === false && <ErrorOutlineIcon className="text-red-500 animate-shake" />}
                </>
              )
            }}
          />
          <FormControl fullWidth margin="normal" error={!!errors.framesSkipped}>
            <InputLabel>Frames Per Second</InputLabel>
            <Select value={framesSkipped} onChange={(e) => setFramesSkipped(e.target.value)} label="Frames Per Second">
              {[...Array(10)].map((_, i) => (
                <MenuItem key={i} value={i + 1}>
                  {i + 1}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors.framesSkipped}</FormHelperText>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />}
            label="Camera Active"
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
            <button
              onClick={() => setOpenAnnotate(true)}
              type="button"
              disabled={!isAnnotateEnabled}
              className={`border-none cursor-pointer text-center px-6 py-2.5 text-white font-semibold rounded-full flex items-center gap-2 transition-all shadow-md hover:shadow-lg ${
                isAnnotateEnabled ? 'bg-[#2e7d32] hover:bg-[#1b5e20] active:bg-[#388e3c] active:scale-95' : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              Annotate
            </button>
          </Box>
        </Box>
      </Modal>

      {/* <Modal open={openAnnotate} onClose={handleCloseAnnotate} fullScreen>
        <div className="flex h-full w-full bg-slate-200 p-4 overflow-auto">
          <div className="w-[90%] h-[90%] pr-2">
            <ImageAnnotationTool 
              cameraImage={cameraImage} 
              cameraId={cameraId} 
              handleCloseAnnotate={handleCloseAnnotate} 
            />
          </div>
          <div className="w-1/3 pr-4">
            <MiddleController
              handleCloseAnnotate={handleCloseAnnotate}
              cameraId={cameraId}
              deviceName={deviceName}
              cameraAddress={cameraAddress}
            />
          </div>
        </div>
      </Modal> */}

      <Modal open={openAnnotate} onClose={handleCloseAnnotate} fullScreen>
        <div className="flex h-full w-full bg-slate-200 p-4 overflow-auto">
          <div className="flex-1 h-full pr-2" style={{ flex: '0 0 75%' }}>
            <ImageAnnotationTool cameraImage={cameraImage} cameraId={cameraId} handleCloseAnnotate={handleCloseAnnotate} />
          </div>

          <div className="flex-1 h-full pr-2" style={{ flex: '0 0 25%' }}>
            <MiddleController
              handleCloseAnnotate={handleCloseAnnotate}
              cameraId={cameraId}
              deviceName={deviceName}
              cameraAddress={cameraAddress}
            />
          </div>
        </div>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddCameraButton;
